import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { LEAVE_STATUS, fmtDateTH, fmtTime, toBE } from './hrShared'

const emptyForm = { os_date: '', os_time_out: '', os_time_return: '', os_purpose: '', os_location: '' }

// การ์ดประวัติออกนอกสถานที่ 1 รายการ สำหรับมุมมองมือถือ
const MyOffsiteCard = ({ r, onCancel, onReturn }) => {
  const ls = LEAVE_STATUS[r.os_status] || LEAVE_STATUS.pending
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="fw-semibold">{fmtDateTH(r.os_date)}</div>
        <Badge bg={ls.bg}>{ls.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">เวลาออก - กลับ:</span> {fmtTime(r.os_time_out)} - {fmtTime(r.os_time_return)}</div>
        <div><span className="text-muted">กลับเข้าจริง:</span> {r.os_actual_return ? fmtTime(r.os_actual_return) : '-'}</div>
        {r.os_purpose && <div><span className="text-muted">ธุระ:</span> {r.os_purpose}</div>}
        {r.os_location && <div><span className="text-muted">สถานที่:</span> {r.os_location}</div>}
        {r.approve_note && <div className="text-muted fs-xs mt-1">{r.approve_note}</div>}
      </div>
      {(r.os_status === 'pending' || (r.os_status === 'approved' && !r.os_actual_return)) && (
        <div className="mt-2 pt-2 border-top d-flex gap-2">
          {r.os_status === 'pending' && (
            <Button size="sm" variant="outline-danger" className="flex-grow-1" onClick={onCancel}>ยกเลิก</Button>
          )}
          {r.os_status === 'approved' && !r.os_actual_return && (
            <Button size="sm" variant="outline-success" className="flex-grow-1" onClick={onReturn}>
              <i className="ri-login-box-line me-1" />กลับแล้ว
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default function MyOffsite() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(String(currentYear))
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/me/offsite?year=${year}`)
      if (res.data.success) setRequests(res.data.data.requests)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = () => {
    setForm({ ...emptyForm, os_date: new Date().toLocaleDateString('en-CA') })
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (form.os_time_return <= form.os_time_out) {
      setModalError('เวลากลับต้องอยู่หลังเวลาออก')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post('/api/hr/me/offsite', form)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: 'ยื่นคำขอแล้ว', text: 'รอการอนุมัติจากงานบุคลากร', timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'ยื่นคำขอไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (r) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกคำขอออกนอกสถานที่?',
      text: `${fmtDateTH(r.os_date)} เวลา ${fmtTime(r.os_time_out)} - ${fmtTime(r.os_time_return)}`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกคำขอ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/hr/me/offsite/${r.os_id}/cancel`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleReturn = async (r) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'บันทึกเวลากลับเข้าสถานที่?',
      text: 'ระบบจะบันทึกเวลาปัจจุบันเป็นเวลากลับเข้าจริง',
      showCancelButton: true,
      confirmButtonText: 'บันทึกเวลากลับ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#198754',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/hr/me/offsite/${r.os_id}/return`)
      Swal.fire({ icon: 'success', title: 'บันทึกเวลากลับแล้ว', timer: 1500, showConfirmButton: false })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-walk-line me-2 text-primary" />
              ขอออกนอกสถานที่
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 130 }} value={year} onChange={e => setYear(e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>พ.ศ. {toBE(y)}</option>)}
              </Form.Select>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />ยื่นคำขอ
              </Button>
            </div>
          </div>

          <Alert variant="light" className="border fs-sm py-2">
            <i className="ri-information-line me-1" />
            สำหรับกรณีมาปฏิบัติงานแล้วต้องออกไปทำธุระนอกสถานที่ระหว่างวัน (ไม่ตัดโควตาวันลา) —
            เมื่อกลับเข้าสถานที่แล้วให้กดบันทึกเวลากลับ
          </Alert>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">ประวัติการขอออกนอกสถานที่ ปี พ.ศ. {toBE(year)}</Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {requests.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีคำขอในปีนี้</div>
                ) : view === 'card' ? (
                  <div className="p-3">
                    <Row className="g-3">
                      {requests.map(r => (
                        <Col key={r.os_id} xs={12} md={6} xl={4}>
                          <MyOffsiteCard r={r} onCancel={() => handleCancel(r)} onReturn={() => handleReturn(r)} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>วันที่</th>
                      <th>เวลาออก - กลับ (โดยประมาณ)</th>
                      <th>กลับเข้าจริง</th>
                      <th>ธุระ</th>
                      <th>สถานที่</th>
                      <th>สถานะ</th>
                      <th style={{ width: 140 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(r => {
                      const ls = LEAVE_STATUS[r.os_status] || LEAVE_STATUS.pending
                      return (
                        <tr key={r.os_id}>
                          <td>{fmtDateTH(r.os_date)}</td>
                          <td>{fmtTime(r.os_time_out)} - {fmtTime(r.os_time_return)}</td>
                          <td>{r.os_actual_return ? fmtTime(r.os_actual_return) : '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 200 }} title={r.os_purpose || ''}>{r.os_purpose || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 150 }} title={r.os_location || ''}>{r.os_location || '-'}</td>
                          <td>
                            <Badge bg={ls.bg}>{ls.label}</Badge>
                            {r.approve_note && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.approve_note}</div>
                            )}
                          </td>
                          <td className="text-end">
                            {r.os_status === 'pending' && (
                              <Button size="sm" variant="outline-danger" onClick={() => handleCancel(r)}>
                                ยกเลิก
                              </Button>
                            )}
                            {r.os_status === 'approved' && !r.os_actual_return && (
                              <Button size="sm" variant="outline-success" onClick={() => handleReturn(r)}>
                                <i className="ri-login-box-line me-1" />กลับแล้ว
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      {/* Submit-request modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ยื่นคำขอออกนอกสถานที่</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}

            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">วันที่</Form.Label>
                <Form.Control size="sm" type="date" required value={form.os_date}
                  onChange={e => setForm(f => ({ ...f, os_date: e.target.value }))} />
              </Col>
              <Col xs={3}>
                <Form.Label className="fs-sm mb-1">เวลาออก</Form.Label>
                <Form.Control size="sm" type="time" required value={form.os_time_out}
                  onChange={e => setForm(f => ({ ...f, os_time_out: e.target.value }))} />
              </Col>
              <Col xs={3}>
                <Form.Label className="fs-sm mb-1">เวลากลับ</Form.Label>
                <Form.Control size="sm" type="time" required value={form.os_time_return}
                  onChange={e => setForm(f => ({ ...f, os_time_return: e.target.value }))} />
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ธุระ / เหตุผล <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} required value={form.os_purpose}
                onChange={e => setForm(f => ({ ...f, os_purpose: e.target.value }))} />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fs-sm mb-1">สถานที่ที่ไป <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={255} value={form.os_location}
                onChange={e => setForm(f => ({ ...f, os_location: e.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
              ยื่นคำขอ
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
